import axios from 'axios';

export type SeveridadeNotificacao = 'critica' | 'atencao' | 'informativa' | 'positiva';

export interface Notificacao {
  id: string;
  tipo: string;
  titulo: string;
  descricao?: string;
  severidade: SeveridadeNotificacao;
  bairro?: string;
  responsavel?: string;
  link?: string;
  referencia: string;
  lida: boolean;
  lida_em?: string;
  created_at: string;
}

export type SeveridadeAlerta = 'critica' | 'atencao' | 'oportunidade';

export interface Alerta {
  id: string;
  severidade: SeveridadeAlerta;
  titulo: string;
  regra: string;
  fonte: string;
  link: string;
  referencia?: string;
  /**
   * Janela real sobre a qual a regra foi avaliada. A maioria das regras não
   * tem recorte temporal, e o texto diz isso — ver `AlertasService`.
   */
  periodo: string;
  /** Marcado como visto neste mandato (`eleg.alerta_leitura`). */
  lida: boolean;
  lida_em?: string;
  /** Destino que de fato cria o que o alerta sugere; ausente quando não há. */
  acao?: { rotulo: string; link: string };
  /** Causa raiz, quando o destino que o alerta pediria não existe. */
  lacuna?: string;
}

export type StatusIntegracao = 'conectada' | 'desconectada' | 'erro' | 'pendente';

export interface Integracao {
  id: string;
  chave: string;
  nome: string;
  descricao?: string;
  status: StatusIntegracao;
  parametros: Record<string, any>;
  ultima_sincronizacao?: string;
  ultimo_erro?: string;
}

export type ModuloCategoria = 'solicitacao' | 'acao' | 'agenda' | 'contato';

export interface Categoria {
  id: string;
  nome: string;
  modulo: ModuloCategoria;
  ativa: boolean;
  ordem: number;
  /** Prazo proprio em dias; null usa o padrao do mandato. */
  sla_dias: number | null;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const notificacoesApi = {
  list: async (token: string, naoLidas = false) => {
    const res = await axios.get<Notificacao[]>(`${API_URL}/core/notificacoes`, {
      ...auth(token),
      params: naoLidas ? { naoLidas: 'true' } : undefined,
    });
    return res.data;
  },
  /** Recalcula as notificações a partir do estado atual do gabinete. */
  gerar: async (token: string) => {
    const res = await axios.post<{ criadas: number; total: number }>(
      `${API_URL}/core/notificacoes/gerar`,
      {},
      auth(token),
    );
    return res.data;
  },
  marcarLida: async (id: string, lida: boolean, token: string) => {
    const res = await axios.put<Notificacao>(
      `${API_URL}/core/notificacoes/${id}`,
      { lida },
      auth(token),
    );
    return res.data;
  },
  marcarTodasLidas: async (token: string) => {
    const res = await axios.post<{ atualizadas: number }>(
      `${API_URL}/core/notificacoes/marcar-todas`,
      {},
      auth(token),
    );
    return res.data;
  },
  remover: async (id: string, token: string) => {
    await axios.delete(`${API_URL}/core/notificacoes/${id}`, auth(token));
  },
};

export const alertasApi = {
  list: async (token: string) => {
    const res = await axios.get<Alerta[]>(`${API_URL}/eleg/alertas`, auth(token));
    return res.data;
  },
  /**
   * Marca (ou desmarca) o alerta como visto. O servidor só aceita o id de uma
   * condição vigente para o mandato, e responde 204 — o estado novo vem do
   * `list` seguinte, não da resposta, para não haver duas versões da verdade.
   */
  marcarLido: async (id: string, lida: boolean, token: string) => {
    await axios.put(
      `${API_URL}/eleg/alertas/${encodeURIComponent(id)}/lido`,
      { lida },
      auth(token),
    );
  },
};

export const integracoesApi = {
  list: async (token: string) => {
    const res = await axios.get<Integracao[]>(`${API_URL}/core/integracoes`, auth(token));
    return res.data;
  },
  update: async (id: string, data: Partial<Integracao>, token: string) => {
    const res = await axios.put<Integracao>(`${API_URL}/core/integracoes/${id}`, data, auth(token));
    return res.data;
  },
};

export const categoriasApi = {
  list: async (token: string, modulo?: ModuloCategoria) => {
    const res = await axios.get<Categoria[]>(`${API_URL}/core/categorias`, {
      ...auth(token),
      params: modulo ? { modulo } : undefined,
    });
    return res.data;
  },
  create: async (
    data: { nome: string; modulo: ModuloCategoria; sla_dias?: number },
    token: string,
  ) => {
    const res = await axios.post<Categoria>(`${API_URL}/core/categorias`, data, auth(token));
    return res.data;
  },
  update: async (id: string, data: Partial<Categoria>, token: string) => {
    const res = await axios.put<Categoria>(`${API_URL}/core/categorias/${id}`, data, auth(token));
    return res.data;
  },
  delete: async (id: string, token: string) => {
    await axios.delete(`${API_URL}/core/categorias/${id}`, auth(token));
  },
};
